// src/components/Navigation.js

import { useState } from 'react'; 
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, LogOut, User, ChevronDown, LayoutDashboard, ShoppingBag, Music, Menu, X, Home, Image, Video, Calendar } from 'lucide-react'; 
import { Link, useLocation } from 'react-router-dom';

export default function Navigation({ 
    isAdmin, 
    onAdminClick, 
    onLogout, 
    mobileMenuOpen,
    setMobileMenuOpen, 
    userDisplayName,
    userPhotoURL,
    currentUser,
    onArtistRegisterClick,
    artistData,
    onBookScheduleClick
}) {
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const location = useLocation();

  const handleLogoutClick = () => {
    setIsMenuOpen(false); 
    onLogout();
  };
  
  const handleArtistRegisterClick = () => {
    setIsMenuOpen(false);
    onArtistRegisterClick();
  };

  const handleBookScheduleClick = () => {
    setIsMenuOpen(false);
    setMobileMenuOpen(false);
    onBookScheduleClick();
  };

  const handleNavigateClick = () => {
    setIsMenuOpen(false);
    setMobileMenuOpen(false);
  };
  
  const isLoggedIn = !!userDisplayName;
  const showProfileDropdown = isLoggedIn;

  // Navigation links
  const navLinks = [
    { label: 'Home', href: '/', icon: Home },
    { label: 'Music', href: '/music', icon: Music },
    { label: 'Videos', href: '/videos', icon: Video },
    { label: 'Gallery', href: '/gallery', icon: Image },
    // { label: 'Members', href: '/members', icon: Users },
    { label: 'Schedules', href: '/schedules', icon: Calendar },
    ...(isLoggedIn ? [{ label: 'My Library', href: '/my-library', icon: ShoppingBag }] : []),
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <motion.nav
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md shadow-lg border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex justify-between items-center">
          
          {/* Logo/Brand - Links to Home */}
          <Link to="/">
            <img
              src="/images/asidors_logo_white.png"
              alt="The Asidors Logo"
              className="h-12 hover:opacity-80 transition" 
            />
          </Link>

          {/* Desktop Nav Links */}
          <div className="flex items-center gap-2">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition font-medium text-sm duration-200 ${
                    isActive(link.href)
                      ? 'bg-white/10 text-white border border-white/20'
                      : 'text-gray-300 hover:text-white hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <Icon size={16} />
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Desktop Right Actions */}
          <div className="flex items-center gap-3">
            
            {/* Book a Schedule Button - Desktop */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBookScheduleClick}
              className="flex items-center justify-center w-10 h-10 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded-lg transition border border-cyan-500/40 hover:border-cyan-500/60 duration-200"
              title="Book a schedule"
            >
              <Calendar size={18} />
            </motion.button>

            {/* Register as Artist Icon Button - Desktop */}
            {isLoggedIn && !isAdmin && !artistData && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleArtistRegisterClick}
                className="flex items-center justify-center w-10 h-10 bg-white/10 hover:bg-white/15 text-white rounded-lg transition border border-white/20 hover:border-white/30 duration-200"
                title="Register as an artist"
              >
                <Music size={18} />
              </motion.button>
            )}

            {/* User Profile Dropdown */}
            {showProfileDropdown ? (
              <div className="relative">
                
                {/* Profile Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="flex items-center space-x-2 text-sm text-white bg-white/10 hover:bg-white/15 px-4 py-2.5 rounded-lg border border-white/20 hover:border-white/30 transition duration-200"
                  title={isAdmin ? `Logged in as Admin` : `Logged in as User`}
                >
                  
                  {/* Avatar */}
                  {userPhotoURL && !photoError ? (
                    <img
                      src={userPhotoURL}
                      alt={userDisplayName}
                      className="w-6 h-6 rounded-full object-cover border border-white/30"
                      onError={() => setPhotoError(true)}
                    />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}

                  {/* Name */}
                  <span className="font-medium truncate max-w-[120px]">
                    {userDisplayName}
                  </span>

                  <ChevronDown size={16} className={`transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : 'rotate-0'}`} />
                </motion.button>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {isMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 rounded-lg shadow-xl bg-gray-900 border border-white/10 ring-1 ring-black/20 z-50 overflow-hidden backdrop-blur-sm"
                    >
                      <div className="py-1">
                        
                        {/* User Info */}
                        <div className="px-4 py-3 text-xs text-gray-400 border-b border-white/10">
                          <div className="text-gray-500 mb-1">Signed in as</div>
                          <span className="font-semibold text-white">{userDisplayName}</span>
                        </div>
                      
                        {/* Book Schedule */}
                        <button
                          onClick={handleBookScheduleClick}
                          className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition duration-150 border-b border-white/5"
                        >
                          <Calendar size={16} className="text-cyan-400" />
                          <span>Book a Schedule</span>
                        </button>

                        {/* Admin Panel */}
                        {isAdmin && (
                            <Link
                              to="/admin"
                              onClick={handleNavigateClick}
                              className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition duration-150 border-b border-white/5"
                            >
                              <LayoutDashboard size={16} className="text-blue-400" />
                              <span>Admin</span>
                            </Link>
                        )}

                        {/* Artist Dashboard */}
                        {currentUser && artistData && !isAdmin && (
                          <Link
                            to="/artist-dashboard"
                            onClick={handleNavigateClick}
                            className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition duration-150 border-b border-white/5"
                          >
                            <Music size={16} className="text-green-400" />
                            <span>Artist Dashboard</span>
                          </Link>
                        )}

                        {/* Register as Artist - In Dropdown */}
                        {!isAdmin && !artistData && (
                          <button
                            onClick={handleArtistRegisterClick}
                            className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition duration-150 border-b border-white/5"
                          >
                            <Music size={16} className="text-purple-400" />
                            <span>Register as Artist</span>
                          </button>
                        )}

                        {/* Logout */}
                        <button
                          onClick={handleLogoutClick}
                          className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition duration-150"
                        >
                          <LogOut size={16} className="text-red-400" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              // Sign In button
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onAdminClick}
                className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-lg transition font-medium text-sm border border-white/20 hover:border-white/30 duration-200"
              >
                <Lock size={16} /> Sign In
              </motion.button>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="lg:hidden flex justify-between items-center">
          
          {/* Logo */}
          <Link to="/">
            <img
              src="/images/asidors_logo_white.png"
              alt="The Asidors Logo"
              className="h-10 hover:opacity-80 transition" 
            />
          </Link>

          {/* Mobile Menu Button & User */}
          <div className="flex items-center gap-2">
            
            {/* Book Schedule Button - Mobile */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleBookScheduleClick}
              className="flex items-center justify-center w-9 h-9 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded-lg transition border border-cyan-500/40 hover:border-cyan-500/60 duration-200"
              title="Book a schedule"
            >
              <Calendar size={16} />
            </motion.button>

            {/* User Profile */}
            {showProfileDropdown && (
              <div className="relative">
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="flex items-center space-x-1.5 text-xs text-white bg-white/10 hover:bg-white/15 px-3 py-2 rounded-lg border border-white/20 hover:border-white/30 transition duration-200"
                >
                  {userPhotoURL && !photoError ? (
                    <img
                      src={userPhotoURL}
                      alt={userDisplayName}
                      className="w-5 h-5 rounded-full object-cover border border-white/30"
                      onError={() => setPhotoError(true)}
                    />
                  ) : (
                    <User className="w-4 h-4" />
                  )}
                  <ChevronDown size={12} className={`transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : 'rotate-0'}`} />
                </motion.button>

                <AnimatePresence>
                  {isMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-48 rounded-lg shadow-xl bg-gray-900 border border-white/10 ring-1 ring-black/20 z-50 backdrop-blur-sm"
                    >
                      <div className="py-1">
                        <div className="px-4 py-3 text-xs text-gray-400 border-b border-white/10">
                          <div className="text-gray-500 mb-1">Signed in as</div>
                          <span className="font-semibold text-white truncate">{userDisplayName}</span>
                        </div>
                      
                        <button
                          onClick={handleBookScheduleClick}
                          className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition duration-150 border-b border-white/5"
                        >
                          <Calendar size={14} className="text-cyan-400" />
                          Book a Schedule
                        </button>

                        {isAdmin && (
                            <Link
                              to="/admin"
                              onClick={handleNavigateClick}
                              className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition duration-150 border-b border-white/5"
                            >
                              <LayoutDashboard size={14} className="text-blue-400" />
                              Admin
                            </Link>
                        )}

                        {currentUser && artistData && !isAdmin && (
                          <Link
                            to="/artist-dashboard"
                            onClick={handleNavigateClick}
                            className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition duration-150 border-b border-white/5"
                          >
                            <Music size={14} className="text-green-400" />
                            Artist Dashboard
                          </Link>
                        )}

                        {!isAdmin && !artistData && (
                          <button
                            onClick={handleArtistRegisterClick}
                            className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition duration-150 border-b border-white/5"
                          >
                            <Music size={14} className="text-purple-400" />
                            Register as Artist
                          </button>
                        )}

                        <button
                          onClick={handleLogoutClick}
                          className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 transition duration-150"
                        >
                          <LogOut size={14} className="text-red-400" />
                          Logout
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Menu Toggle */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-white hover:bg-white/10 rounded-lg transition duration-200"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden mt-4 pb-4 border-t border-white/10"
            >
              <div className="flex flex-col gap-1.5 pt-4">
                
                {/* Mobile Nav Links */}
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={handleNavigateClick}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition font-medium text-sm duration-200 ${
                        isActive(link.href)
                          ? 'bg-white/10 text-white border border-white/20'
                          : 'text-gray-300 hover:text-white hover:bg-white/5 border border-transparent'
                      }`}
                    >
                      <Icon size={16} />
                      {link.label}
                    </Link>
                  );
                })}

                {/* Mobile Sign In */}
                {!showProfileDropdown && (
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      onAdminClick();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-lg transition font-medium text-sm border border-white/20 hover:border-white/30 mt-2 duration-200"
                  >
                    <Lock size={16} /> Sign In
                  </motion.button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Click outside to close dropdown */}
      {isMenuOpen && (
          <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
      )}
    </motion.nav>
  );
}